import axios from 'axios';

export interface ForecastParams {
  page_size?: number;
  page_no?: number;
  search_id: number;
  产品编码: string;
}

export interface ForecastResponse {
  res_status_code: string;
  res_message: string;
  res_content: any;
  exception_message: string | null;
}

// 在售商品接口
export interface OnSaleProduct {
  月份: string;
  销售数量: number;
  brand_code: string;
  品牌: string;
  大分类?: string; // 可选字段，某些接口可能不返回
  中分类?: string;
  小分类?: string;
  规格?: string; // 某些接口返回规格而不是分类信息
  relation_spu_no: string;
  主SPU名称: string;
  spu_no: string;
  SPU名称: string;
  dim_goods_no: string;
  产品名称: string;
}

// 在售商品SPU接口
export interface OnSaleSpuProduct {
  月份: string;
  销售数量: number;
  brand_code: string;
  品牌: string;
  大分类: string;
  中分类: string;
  小分类: string;
  relation_spu_no: string;
  主SPU名称: string;
  spu_no: string;
  SPU名称: string;
  dim_goods_no: string;
  产品名称: string;
}

export interface OnSaleProductsParams {
  page_size?: number;
  page_no?: number;
  search_id: number;
  月份?: string;
  品牌编码?: string; // 品牌编码参数
}

// 定义所有图表的search_id
export const FORECAST_SEARCH_IDS = {
  SKU_SALES: 1062,      // SKU销量预测
  SPU_SALES: 1068,      // SPU销量预测
  SP_PLAN: 1071,        // SP计划表
  SPU_MARKETING: 1059,  // SPU营销数据
  WEEKLY_SALES: 1072,   // SKU推广金额&每周销量
  SKU_WEEKLY: 1072,     // SKU推广金额&每周销量
  SKU_DAILY: 1073,      // SKU推广金额&每日销量
  SKU_PRICE_DAILY: 1109,  // SKU价格&每日销量
  PLATFORM_WEEKLY: 1067,   // 每周平台销量分布
  TWO_YEAR_SPU: 1080,       // 两年内SPU销量数量
  TWO_YEAR_CATEGORY3: 1081,  // 两年内分类3销量情况
  TWO_YEAR_SPEC_SALES: 1099,    // 两年内规格销量情况
  TWO_YEAR_SPU_SPEC_SALES: 1091,  // 两年内SPU规格销量情况
  ON_SALE_PRODUCTS: 1092,  // 在售商品情况
  ON_SALE_SPU_PRODUCTS: 1093,  // 在售商品SPU情况
  FORECAST_MAP_STATUS: 1121  // 预测MAPE情况
};

// 创建axios实例
const forecastApi = axios.create({
  baseURL: 'https://gate.shjinjia.com.cn/api',
  timeout: 10000
});

// 请求拦截器
forecastApi.interceptors.request.use(
  (config) => {
    // 这里可以添加token等认证信息
    if (config.headers) {
      config.headers['token'] = 'be8cb528e6c8466e8866eafe3a55ac8e';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
forecastApi.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (data.res_status_code === '2000' && data.res_message === '登录已过期，请重新登录！') {
      // 处理登录过期情况
      // TODO: 重定向到登录页或刷新token
      return Promise.reject(new Error('登录已过期'));
    }
    return data;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const getForecastData = async (params: ForecastParams): Promise<ForecastResponse> => {
  try {
    const response = await forecastApi.get('/SearchTool/ExecuteSearchToolInstance', {
      params: {
        page_size: params.page_size || 50,
        page_no: params.page_no || 1,
        search_id: params.search_id,
        产品编码: params.产品编码
      }
    });
    return response;
  } catch (error) {
    console.error('Forecast API Error:', error);
    throw error;
  }
};

// 获取在售商品数据
export const getOnSaleProducts = async (params: OnSaleProductsParams): Promise<ForecastResponse> => {
  try {
    const requestParams: any = {
      page_size: params.page_size || 1000, // 获取更多数据
      page_no: params.page_no || 1,
      search_id: params.search_id,
    };

    // 添加可选参数
    if (params.月份) {
      requestParams.月份 = params.月份;
    }

    if (params.品牌编码) {
      requestParams.品牌编码 = params.品牌编码; // 添加品牌编码参数
    }

    const response = await forecastApi.get('/SearchTool/ExecuteSearchToolInstance', {
      params: requestParams
    });
    return response;
  } catch (error) {
    console.error('On Sale Products API Error:', error);
    throw error;
  }
};

// 获取在售商品SPU数据
export const getOnSaleSpuProducts = async (params: OnSaleProductsParams): Promise<ForecastResponse> => {
  try {
    const requestParams: any = {
      page_size: params.page_size || 1000, // 获取更多数据
      page_no: params.page_no || 1,
      search_id: params.search_id,
    };

    // 添加可选参数
    if (params.月份) {
      requestParams.月份 = params.月份;
    }

    if (params.品牌编码) {
      requestParams.品牌编码 = params.品牌编码; // 添加品牌编码参数
    }

    const response = await forecastApi.get('/SearchTool/ExecuteSearchToolInstance', {
      params: requestParams
    });
    return response;
  } catch (error) {
    console.error('On Sale SPU Products API Error:', error);
    throw error;
  }
};

// 预测MAPE情况数据接口
export interface ForecastMapData {
  月份: string;
  品牌: string;
  品牌名称?: string;
  产品编码: string;
  MAPE: number;
  [key: string]: any; // 允许其他字段
}

export interface ForecastMapParams {
  page_size?: number;
  page_no?: number;
  search_id: number;
  product_code?: string;
  brand_code?: string;

}

// 获取预测MAPE情况数据
export const getForecastMapData = async (params: ForecastMapParams): Promise<ForecastResponse> => {
  try {
    const requestParams: any = {
      page_size: params.page_size || 50,
      page_no: params.page_no || 1,
      search_id: params.search_id,
      '@product_code': params.product_code || '',
      '@brand_code': params.brand_code || '',
      '@target_month_from':'2025-02',
      '@target_month_to':'2026-01'
    };

    // 添加可选参数
    if (params.月份) {
      requestParams.月份 = params.月份;
    }

    if (params.品牌编码) {
      requestParams.品牌编码 = params.品牌编码;
    }

    const response = await forecastApi.get('/SearchTool/ExecuteSearchToolInstance', {
      params: requestParams
    });
    console.log('response',response);
    return response;
  } catch (error) {
    console.error('Forecast MAP API Error:', error);
    throw error;
  }
};

// 批量获取所有图表数据
export const getAllForecastData = async (productCode: string) => {
  const requests = Object.values(FORECAST_SEARCH_IDS).map(searchId => 
    getForecastData({
      search_id: searchId,
      产品编码: productCode
    })
  );

  try {
    const results = await Promise.allSettled(requests);
    return results.map((result, index) => ({
      searchId: Object.values(FORECAST_SEARCH_IDS)[index],
      data: result.status === 'fulfilled' ? result.value : null,
      error: result.status === 'rejected' ? result.reason : null
    }));
  } catch (error) {
    console.error('Batch Forecast API Error:', error);
    throw error;
  }
};
